import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { COUNTRY_CODE_TO_NAME } from "../../components/MarketOverviewPage/data/constants";

/**
 * Convert country code to country name
 * @param {string} code - Country code (e.g., "US", "UK")
 * @returns {string} Country name or original code if not found
 */
const getCountryName = (code) => COUNTRY_CODE_TO_NAME[code] || code;

/**
 * Custom hook for accessing workspace market data with region hierarchy
 * Provides helper functions for getting markets by client/region
 *
 * @returns {Object} Workspace market data and helper functions
 */
export const useWorkspaceMarkets = () => {
  // Get workspace data from Redux
  const {
    allWorkspaces,
    hierarchicalWorkspaces,
    regionMarketData: reduxRegionMarketData,
    fetchingWorkspaces,
  } = useSelector((state) => state.workspace);

  // Get current user's id_client from auth state
  const currentIdClient = useSelector((state) => state.auth?.id_client);

  /**
   * Get all unique workspace (client) options for dropdown
   * @returns {Array} [{ value, label, clientData }]
   */
  const workspaceOptions = useMemo(() => {
    if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
      return [];
    }
    return Object.entries(hierarchicalWorkspaces).map(
      ([clientId, clientData]) => {
        // Count total markets across all regions
        const totalMarkets = Object.values(clientData.regions).reduce(
          (sum, region) => sum + region.markets.length,
          0
        );
        return {
          value: clientId,
          label: `${clientId} (${totalMarkets} markets)`,
          clientData,
        };
      }
    );
  }, [hierarchicalWorkspaces]);

  /**
   * Get region-market data for a specific workspace (client)
   * @param {string} workspaceId - The client ID
   * @returns {Object} { [regionName]: { markets: [...], scopes: {...} } }
   */
  const getRegionMarketDataForWorkspace = useCallback(
    (workspaceId) => {
      if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
        return {};
      }
      const client = hierarchicalWorkspaces[workspaceId];
      if (!client) return {};

      const result = {};
      Object.entries(client.regions).forEach(([regionName, regionData]) => {
        // Convert country codes to country names
        const marketNames = regionData.markets.map(getCountryName);
        result[regionName] = {
          markets: marketNames,
          scopes: marketNames.reduce((acc, market) => {
            acc[market] = "Deepdive; Execution view; Market Overview";
            return acc;
          }, {}),
        };
      });
      return result;
    },
    [hierarchicalWorkspaces]
  );

  /**
   * Get flat list of markets for a specific workspace (client)
   * @param {string} workspaceId - The client ID
   * @param {boolean} asCountryNames - If true, return country names instead of codes (default: true)
   * @returns {Array} Array of market names or codes
   */
  const getMarketsForWorkspace = useCallback(
    (workspaceId, asCountryNames = true) => {
      if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
        return [];
      }
      const client = hierarchicalWorkspaces[workspaceId];
      if (!client) return [];

      const markets = [];
      Object.values(client.regions).forEach((region) => {
        region.markets.forEach((market) => {
          const marketValue = asCountryNames ? getCountryName(market) : market;
          if (!markets.includes(marketValue)) {
            markets.push(marketValue);
          }
        });
      });
      return markets;
    },
    [hierarchicalWorkspaces]
  );

  /**
   * Get regions for a specific workspace (client)
   * @param {string} workspaceId - The client ID
   * @returns {Array} Array of region names
   */
  const getRegionsForWorkspace = useCallback(
    (workspaceId) => {
      if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
        return [];
      }
      const client = hierarchicalWorkspaces[workspaceId];
      if (!client) return [];
      return Object.keys(client.regions);
    },
    [hierarchicalWorkspaces]
  );

  /**
   * Get markets for a specific region within a workspace
   * @param {string} workspaceId - The client ID
   * @param {string} regionName - The region name
   * @returns {Array} Array of market names
   */
  const getMarketsForRegion = useCallback(
    (workspaceId, regionName) => {
      if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
        return [];
      }
      const client = hierarchicalWorkspaces[workspaceId];
      if (!client || !client.regions[regionName]) return [];
      return client.regions[regionName].markets;
    },
    [hierarchicalWorkspaces]
  );

  /**
   * Generate TreeSelect data for a workspace with region -> market hierarchy
   * @param {string} workspaceId - The client ID
   * @returns {Array} TreeSelect compatible data structure
   */
  const generateTreeDataForWorkspace = useCallback(
    (workspaceId) => {
      if (!hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object') {
        return [
          {
            value: "no_data",
            title: "No data available",
            key: "no_data",
            disabled: true,
          },
        ];
      }
      const client = hierarchicalWorkspaces[workspaceId];
      if (!client) {
        return [
          {
            value: "no_data",
            title: "No data available",
            key: "no_data",
            disabled: true,
          },
        ];
      }

      const regions = Object.entries(client.regions);
      if (regions.length === 0) {
        return [
          {
            value: "no_markets",
            title: "No markets available for this workspace",
            key: "no_markets",
            disabled: true,
          },
        ];
      }

      // Build hierarchical tree: Region -> Markets
      return regions.map(([regionName, regionData]) => ({
        value: `region-${regionName}`,
        title: regionName,
        key: `region-${regionName}`,
        selectable: false, // Region itself is not selectable, only for grouping
        children: regionData.markets.map((market) => {
          const countryName = getCountryName(market);
          return {
            value: countryName.toLowerCase(),
            title: countryName,
            key: countryName.toLowerCase(),
            country_code: market,
          };
        }),
      }));
    },
    [hierarchicalWorkspaces]
  );

  /**
   * Generate flat TreeSelect data (no region grouping) for a workspace
   * @param {string} workspaceId - The client ID
   * @returns {Array} Flat TreeSelect compatible data structure
   */
  const generateFlatTreeDataForWorkspace = useCallback(
    (workspaceId) => {
      const markets = getMarketsForWorkspace(workspaceId);
      if (!markets || markets.length === 0) {
        return [
          {
            value: "no_markets",
            title: "No markets available for this workspace",
            key: "no_markets",
            disabled: true,
          },
        ];
      }

      return markets.map((market) => {
        const countryName = getCountryName(market);
        return {
          value: countryName.toLowerCase(),
          title: countryName,
          key: countryName.toLowerCase(),
          country_code: market,
        };
      });
    },
    [getMarketsForWorkspace]
  );

  /**
   * Get global region-market data (merged from all workspaces)
   * Compatible with existing regionMarketData format in useUserData
   * @returns {Object} { [regionName]: { markets: [...], scopes: {...} } }
   */
  const globalRegionMarketData = useMemo(() => {
    return reduxRegionMarketData || {};
  }, [reduxRegionMarketData]);

  /**
   * Get region-market data filtered by current user's id_client
   * Only includes markets from workspaces matching state.auth.id_client
   * @returns {Object} { [regionName]: { markets: [...], scopes: {...} } }
   */
  const currentClientRegionMarketData = useMemo(() => {
    if (!currentIdClient || !hierarchicalWorkspaces || typeof hierarchicalWorkspaces !== 'object' || !hierarchicalWorkspaces[currentIdClient]) {
      return {};
    }
    return getRegionMarketDataForWorkspace(currentIdClient);
  }, [
    currentIdClient,
    hierarchicalWorkspaces,
    getRegionMarketDataForWorkspace,
  ]);

  /**
   * Check if a market belongs to a specific region in a workspace
   * @param {string} workspaceId - The client ID
   * @param {string} regionName - The region name
   * @param {string} market - The market name
   * @returns {boolean}
   */
  const isMarketInRegion = useCallback(
    (workspaceId, regionName, market) => {
      const markets = getMarketsForRegion(workspaceId, regionName);
      return markets.includes(market);
    },
    [getMarketsForRegion]
  );

  return {
    // Raw data
    allWorkspaces,
    hierarchicalWorkspaces,
    globalRegionMarketData,
    currentClientRegionMarketData, // Filtered by current user's id_client
    currentIdClient,
    fetchingWorkspaces,

    // Computed options
    workspaceOptions,

    // Helper functions
    getRegionMarketDataForWorkspace,
    getMarketsForWorkspace,
    getRegionsForWorkspace,
    getMarketsForRegion,
    generateTreeDataForWorkspace,
    generateFlatTreeDataForWorkspace,
    isMarketInRegion,
  };
};

export default useWorkspaceMarkets;
